"use client";

import { c as _c } from "react-compiler-runtime";
import { createDefaultImageReader, createDefaultImageWriter, type PinturaDefaultImageWriterResult, type PinturaImageState, plugin_crop, setPlugins } from "@pqina/pintura";
import { LocaleCore, LocaleCrop } from "@pqina/pintura/locale/en_GB";
import { PinturaEditor } from "@pqina/react-pintura";
import { Crop, X } from "lucide-react";
import { useRef } from "react";
import "@pqina/pintura/pintura.css";
import { Button } from "@/components/primitives";
import { useMediaQuery } from "@/core/hooks/useMediaQuery/useMediaQuery";
setPlugins(plugin_crop);

/**
 * Which surface the editor's controls sit on. The editor frame is always dark, but the caption and
 * the cancel button render BELOW it, on whatever the host step provides: the capture flows put it on
 * a dark overlay, the wellness step renders it inline on the ordinary page. Hard-coding white left
 * those two controls invisible on the light step, so the tone picks the palette.
 */
type ImageCropEditorTone = "surface" | "overlay";
const captionClassByTone: Record<ImageCropEditorTone, string> = {
  surface: "text-muted-foreground",
  overlay: "text-overlay-foreground/70"
};

// The outline variant already carries the on-surface border and text colors, so only the overlay
// tone overrides them.
const cancelClassByTone: Record<ImageCropEditorTone, string | undefined> = {
  surface: undefined,
  overlay: "border-overlay-foreground/40 text-overlay-foreground hover:border-overlay-foreground hover:text-overlay-foreground"
};
type ImageCropEditorProps = {
  imageSrc: string;
  targetWidth: number;
  targetHeight: number;
  quality: number;
  cancelLabel: string;
  confirmLabel: string;
  dragToCropLabel: string;
  cropAspectRatio?: number;
  initialImageState?: PinturaImageState;
  tone?: ImageCropEditorTone;
  onCropComplete: (file: File) => void;
  onCancel: () => void;
};
export default function ImageCropEditor(t0) {
  const $ = _c(40);
  const {
    imageSrc,
    targetWidth,
    targetHeight,
    quality,
    cancelLabel,
    confirmLabel,
    dragToCropLabel,
    cropAspectRatio,
    initialImageState,
    tone: t1,
    onCropComplete,
    onCancel
  } = t0;
  const tone = t1 === undefined ? "surface" : t1;
  const {
    mobile
  } = useMediaQuery();
  const editorRef = useRef(null);
  let t2;
  if ($[0] !== onCropComplete) {
    t2 = result => {
      const croppedFile = result.dest as File;
      onCropComplete(croppedFile);
    };
    $[0] = onCropComplete;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleProcess = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      editorRef.current?.editor.processImage();
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleCropAndContinue = t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["crop" as const];
    t5 = createDefaultImageReader();
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== quality || $[6] !== targetHeight || $[7] !== targetWidth) {
    t6 = createDefaultImageWriter({
      mimeType: "image/jpeg",
      quality,
      targetSize: {
        width: targetWidth,
        height: targetHeight,
        fit: "contain",
        upscale: false
      }
    });
    $[5] = quality;
    $[6] = targetHeight;
    $[7] = targetWidth;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ...LocaleCore,
      ...LocaleCrop
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== cropAspectRatio || $[11] !== initialImageState || $[12] !== t6) {
    t8 = {
      utils: t4,
      imageReader: t5,
      imageWriter: t6,
      imageCropAspectRatio: cropAspectRatio,
      imageCropLimitToImage: true,
      imageState: initialImageState,
      cropEnableButtonFlipHorizontal: false,
      cropEnableButtonRotateLeft: false,
      cropEnableButtonRotateRight: false,
      cropEnableRotationInput: false,
      cropEnableZoom: true,
      cropEnableZoomInput: true,
      enableToolbar: false,
      enableButtonExport: false,
      locale: t7
    };
    $[10] = cropAspectRatio;
    $[11] = initialImageState;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const editorDefaults = t8;
  const t9 = cancelClassByTone[tone];
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <X className="mr-1.5 h-4 w-4" />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== cancelLabel) {
    t11 = <span className="type-body-sm">{cancelLabel}</span>;
    $[15] = cancelLabel;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== onCancel || $[18] !== t11 || $[19] !== t9) {
    t12 = <Button className={t9} size="sm" variant="outline" onClick={onCancel}>{t10}{t11}</Button>;
    $[17] = onCancel;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const cancelButton = t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Crop className="mr-1.5 h-4 w-4" />;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== confirmLabel) {
    t14 = <Button size="sm" onClick={handleCropAndContinue}>{t13}<span className="type-body-sm">{confirmLabel}</span></Button>;
    $[22] = confirmLabel;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const cropButton = t14;
  let t15;
  if ($[24] !== editorDefaults || $[25] !== handleProcess || $[26] !== imageSrc) {
    t15 = <div className="overflow-hidden rounded-2xl bg-black"><div className="aspect-3/4"><PinturaEditor {...editorDefaults} ref={editorRef} src={imageSrc} onProcess={handleProcess} /></div></div>;
    $[24] = editorDefaults;
    $[25] = handleProcess;
    $[26] = imageSrc;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const t16 = `type-caption ${captionClassByTone[tone]}`;
  let t17;
  if ($[28] !== dragToCropLabel || $[29] !== t16) {
    t17 = <p className={t16}>{dragToCropLabel}</p>;
    $[28] = dragToCropLabel;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = mobile ? cropButton : cancelButton;
  const t19 = mobile ? cancelButton : cropButton;
  let t20;
  if ($[31] !== t18 || $[32] !== t19) {
    t20 = <div className="flex flex-col items-center gap-3 md:flex-row">{t18}{t19}</div>;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t17 || $[35] !== t20) {
    t21 = <div className="flex flex-col items-center gap-4">{t17}{t20}</div>;
    $[34] = t17;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t15 || $[38] !== t21) {
    t22 = <div className="flex flex-col gap-4">{t15}{t21}</div>;
    $[37] = t15;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  return t22;
}
export type { ImageCropEditorProps, ImageCropEditorTone };